import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiYoutube, FiMail, FiPhone, FiMapPin, FiHeart, FiShield, FiAward, FiExternalLink, FiDownload} = FiIcons;

const Footer = () => {
  const socialLinks = [
    {icon: FiFacebook, href: '#', label: 'Facebook'},
    {icon: FiTwitter, href: '#', label: 'Twitter'},
    {icon: FiInstagram, href: '#', label: 'Instagram'},
    {icon: FiLinkedin, href: '#', label: 'LinkedIn'},
    {icon: FiYoutube, href: '#', label: 'YouTube'}
  ];

  const quickLinks = [
    {name: 'About Us', path: '/about'},
    {name: 'Our Programs', path: '/our-work'},
    {name: 'Success Stories', path: '/impact'},
    {name: 'Annual Reports', path: '/reports'},
    {name: 'Volunteer', path: '/volunteer'},
    {name: 'Careers', path: '/careers'}
  ];

  const legalLinks = [
    {name: 'Privacy Policy', path: '/privacy'},
    {name: 'Terms of Service', path: '/terms'},
    {name: 'Refund Policy', path: '/refund'},
    {name: 'Cookie Policy', path: '/cookies'}
  ];

  const certifications = [
    {name: '80G Certified', icon: FiShield},
    {name: 'Section 8 Company', icon: FiAward},
    {name: '12A Registration', icon: FiShield}
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-4 mb-6 group">
                <motion.div 
                  whileHover={{scale: 1.05, rotate: 5}} 
                  className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-premium transition-all duration-300 bg-white"
                >
                  <img 
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759495915116-Begging%20%20Free%20India.png"
                    alt="Begging Free India Foundation Logo"
                    className="w-full h-full object-contain p-1"
                  />
                </motion.div>
                <div>
                  <div className="typography-heading text-2xl">Begging Free India</div>
                  <div className="text-orange-300 font-medium">Foundation</div>
                </div>
              </Link>

              <p className="typography-body text-gray-300 mb-8 max-w-md leading-relaxed">
                India's premier rehabilitation foundation transforming lives through dignity, empowerment, and sustainable solutions. Join our movement for a begging-free India where every citizen thrives.
              </p>

              {/* Certifications */}
              <div className="mb-8">
                <h4 className="typography-caption text-orange-300 mb-4">Certifications & Trust</h4>
                <div className="flex flex-wrap gap-4">
                  {certifications.map((cert, index) => (
                    <motion.div
                      key={cert.name}
                      whileHover={{scale: 1.05}}
                      className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20"
                    >
                      <SafeIcon icon={cert.icon} className="w-4 h-4 text-orange-400" />
                      <span className="text-sm font-medium">{cert.name}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h4 className="typography-caption text-orange-300 mb-4">Follow Our Journey</h4>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      whileHover={{scale: 1.1, y: -2}}
                      whileTap={{scale: 0.9}}
                      className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-orange-600 transition-all duration-300 group"
                      aria-label={social.label}
                    >
                      <SafeIcon icon={social.icon} className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="typography-heading text-lg mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className="typography-body text-gray-300 hover:text-orange-300 transition-colors duration-200 flex items-center group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-200">
                        {item.name}
                      </span>
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact & Resources */}
            <div>
              <h3 className="typography-heading text-lg mb-6">Get in Touch</h3>
              
              {/* Contact Info */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-orange-400 mt-1 flex-shrink-0" />
                  <div className="text-sm text-gray-300">
                    123 Gandhi Nagar<br />
                    New Delhi, India 110001
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 text-orange-400" />
                  <a href="tel:+919876543210" className="text-sm text-gray-300 hover:text-orange-300 transition-colors duration-200">
                    +91 98765 43210
                  </a>
                </div>
                
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-orange-400" />
                  <a href="mailto:info@beggingfreeindia.org" className="text-sm text-gray-300 hover:text-orange-300 transition-colors duration-200">
                    info@beggingfreeindia.org
                  </a>
                </div>
              </div>

              {/* Resources */}
              <div>
                <h4 className="typography-caption text-orange-300 mb-4">Resources</h4>
                <div className="space-y-3">
                  <motion.a
                    href="#"
                    whileHover={{x: 5}}
                    className="flex items-center space-x-2 text-sm text-gray-300 hover:text-orange-300 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Annual Report 2024</span>
                  </motion.a>
                  
                  <motion.a
                    href="#"
                    whileHover={{x: 5}}
                    className="flex items-center space-x-2 text-sm text-gray-300 hover:text-orange-300 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Impact Assessment</span>
                  </motion.a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-gray-700 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="typography-heading text-2xl mb-4">Stay Connected</h3>
              <p className="typography-body text-gray-300">
                Get monthly updates on our impact, success stories, and ways to get involved in transforming lives.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="input-premium flex-1 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder-gray-400"
              />
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="btn-premium-primary px-8 whitespace-nowrap"
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">
              © 2024 Begging Free India Foundation. All rights reserved. | Reg. No: U85300DL2024NPL123456
            </div>
            <div className="flex flex-wrap gap-6">
              {legalLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="text-sm text-gray-400 hover:text-orange-300 transition-colors duration-200"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;